import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Card, Col, notification, Radio, Row, Tag, Typography } from "antd";
import { MailOutlined, WhatsAppOutlined } from "@ant-design/icons";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import LabelText from "../../../../components/input/LabelText";
import LabelTextarea from "../../../../components/input/LabelTextarea";
import BotonSubmit from "../../../../components/Boton/BotonSubmit";
import {
  startEnviarMensajeTexto,
  startGetAceptacionNotificacion,
  startSolicitudNotificacionesAsistenciaWA,
} from "../../../../context/actions/detalleActions";
import PlantillasSMS from "./PlantillasSMS";

export const ComunicacionSMS = () => {
  const { asistenciaID } = useParams();
  const dispatch = useDispatch();
  const { asistencia, aceptacionNotificacion } = useSelector((state) => state.detalleReducer);
  const [telefono, setTelefono] = useState("");
  const [mensaje, setMensaje] = useState("");
  const [esWA, setEsWA] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingPlantilla, setLoadingPlantilla] = useState(false);
  const pollingRef = useRef(null);

  const telefonoAsistencia = asistencia?.telefono || "";
  const telefonoTieneWA = Boolean(asistencia?.telefonoEsWA);
  const aceptadoWA = Boolean(aceptacionNotificacion?.fechaAceptacion);
  const whatsappPendiente = telefonoTieneWA && !aceptadoWA;
  const whatsappSinAceptar = esWA && !aceptadoWA;

  const detenerPollingAceptacion = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const consultarAceptacion = useCallback(async () => {
    const result = await dispatch(startGetAceptacionNotificacion(asistenciaID, false));

    if (result.code === 0 && result.data?.fechaAceptacion) {
      detenerPollingAceptacion();
    }

    return result;
  }, [asistenciaID, detenerPollingAceptacion, dispatch]);

  const iniciarPollingAceptacion = useCallback(() => {
    detenerPollingAceptacion();

    consultarAceptacion();

    pollingRef.current = setInterval(() => {
      consultarAceptacion();
    }, 20000);
  }, [consultarAceptacion, detenerPollingAceptacion]);

  useEffect(() => {
    setTelefono(telefonoAsistencia);
  }, [telefonoAsistencia]);

  useEffect(() => {
    if (aceptadoWA) {
      detenerPollingAceptacion();
    }
  }, [aceptadoWA, detenerPollingAceptacion]);

  useEffect(() => {
    if (whatsappPendiente) {
      iniciarPollingAceptacion();
    }
  }, [whatsappPendiente, iniciarPollingAceptacion]);

  useEffect(() => {
    return () => detenerPollingAceptacion();
  }, [detenerPollingAceptacion]);

  const handlePlantillaClick = (plantilla) => {
    setMensaje(plantilla.mensaje);
  };

  const handleClick = async () => {
    if (telefono === "" || mensaje === "") {
      notification.warning({
        message: "Campos incompletos",
        description: "Por favor completa todos los campos",
        placement: "bottomRight",
      });
      return;
    }

    if (!/^\d{10}$/.test(telefono)) {
      notification.warning({
        message: "Teléfono inválido",
        description: "El teléfono debe tener 10 dígitos",
        placement: "bottomRight",
      });
      return;
    }

    setLoading(true);

    try {
      const response = await dispatch(startEnviarMensajeTexto({
        telefono,
        mensaje,
        asistenciaID,
        esWA,
      }));

      if (response.code === 0) {
        setMensaje("");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEnviarPlantillaWA = async () => {
    if (!telefono) {
      notification.warning({
        message: "Teléfono inválido",
        description: "No hay teléfono registrado para WhatsApp",
        placement: "bottomRight",
      });
      return;
    }

    setLoadingPlantilla(true);

    try {
      const response = await dispatch(startSolicitudNotificacionesAsistenciaWA({
        asistenciaID,
        telefono,
      }));

      if (response.code === 0) {
        iniciarPollingAceptacion();
      }
    } finally {
      setLoadingPlantilla(false);
    }
  };

  return (
    <Card
      bordered={false}
      style={{ maxWidth: 820 }}
      bodyStyle={{ padding: 24 }}
    >
      <div style={{ marginBottom: 24 }}>
        <Typography.Title level={5} style={{ marginBottom: 4 }}>
          Enviar mensaje al cliente
        </Typography.Title>
        <Typography.Text type="secondary">
          Captura el teléfono y utiliza una plantilla si lo necesitas.
        </Typography.Text>
      </div>

      <Row gutter={[20, 18]} align="bottom">
        <Col xs={24} md={9}>
          <div className="inputLabel" style={{ marginBottom: 8 }}>
            Canal
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Radio.Group
              value={esWA}
              onChange={({ target }) => setEsWA(target.value)}
              disabled={loading}
              buttonStyle="solid"
              style={{ width: "100%" }}
            >
              <Radio.Button value={false} style={{ width: "50%", textAlign: "center" }}>
                SMS
              </Radio.Button>
              <Radio.Button value={true} style={{ width: "50%", textAlign: "center" }}>
                WhatsApp
              </Radio.Button>
            </Radio.Group>
          </div>
        </Col>

        <Col xs={24} md={15}>
          <LabelText
            label="Numero de teléfono"
            name="telefono"
            value={telefono}
            onChange={({ target }) => setTelefono(target.value)}
            placeholder="5532229610"
            disabled={loading || esWA}
          />
        </Col>

        {whatsappSinAceptar && (
          <Col xs={24}>
            <Tag color="orange">Pendiente de aceptación</Tag>
            <Button
              size="small"
              style={{ marginLeft: 8 }}
              loading={loadingPlantilla}
              disabled={loading}
              onClick={handleEnviarPlantillaWA}
            >
              Enviar plantilla WhatsApp
            </Button>
          </Col>
        )}

        <Col xs={24}>
          <LabelTextarea
            label="Mensaje"
            name="mensaje"
            value={mensaje}
            onChange={({ target }) => setMensaje(target.value)}
            placeholder="Escribe tu mensaje aqui..."
            rows={3}
            disabled={loading}
          />
        </Col>

        <Col xs={24}>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <BotonSubmit
              onClick={handleClick}
              tituloBoton="Enviar"
              icon={esWA ? <WhatsAppOutlined /> : <MailOutlined />}
              loading={loading}
              disabled={whatsappSinAceptar}
            />
          </div>
        </Col>
      </Row>

      <PlantillasSMS onSelect={handlePlantillaClick} />
    </Card>
  );
};
